<script setup lang="ts">
import { computed } from "vue"

interface Props {
  id: string // Unique ID
  value?: string
  modelValue?: string | string[] // Bound model value unique
  name?: string
  disabled?: boolean
  ariaLabel?: string // Aria label is needed if no label is present for accessibility
}

const props = withDefaults(defineProps<Props>(), {
  name: "radio",
  modelValue: "",
  disabled: false
})

const emit = defineEmits<{
  "update:modelValue": [value: string]
}>()

function updateValue() {
  if (!props.disabled) {
    emit("update:modelValue", props.value ?? "")
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    updateValue()
  }
}

const isChecked = computed(() => (
  Array.isArray(props.modelValue)
    ? props.modelValue.includes(props.value ?? "")
    : props.modelValue === props.value
))
</script>

<template>
  <div
    class="group/kradio relative flex cursor-pointer items-center rounded-full border-2 border-transparent bg-white px-4 py-2 ring-offset-2 hover:bg-gray-300 focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:outline-none"
    :aria-checked="isChecked"
    role="radio"
    tabindex="0"
    :class="{
      'pointer-events-none cursor-not-allowed': disabled,
      'border-2 border-violet-700 font-bold': isChecked,
    }"
    :aria-labelledby="props.ariaLabel ? undefined : `${props.id}-label`"
    :aria-label="props.ariaLabel"
    @click="updateValue"
    @keydown="handleKeydown"
  >
    <input
      :id="props.id"
      type="radio"
      :name="props.name"
      :value="props.value"
      :checked="isChecked"
      class="sr-only"
      :disabled="disabled"
      tabindex="-1"
      :aria-label="props.ariaLabel"
    >

    <label
      :id="`${props.id}-label`"
      :for="props.id"
      class="flex cursor-pointer items-center pr-1 leading-tight text-dark peer-checked:font-bold disabled:text-gray-700"
      :class="{
        'cursor-not-allowed text-gray-700': disabled,
      }"
    >
      <span
        class="z-20 mr-2 inline-block size-5 shrink-0 rounded-full border border-violet-700 ring-2 ring-white ring-inset group-hover/kradio:border-violet-900"
        :class="{
          'cursor-not-allowed border-gray-700!': disabled,
          'border-gray-700! bg-gray-700!': disabled && isChecked,
          'hover:bg-violet-900': isChecked,
        }"
      />
      <slot />
    </label>
  </div>
</template>

<style scoped>
@reference "@/style.css";

input[type="radio"]:checked + label span {
  @apply bg-violet-700;
}

input[type="radio"]:checked + label span:hover {
  @apply bg-violet-900;
}

.disabled input[type="radio"]:checked + label span {
  @apply bg-violet-900;
}

input[type="radio"]:checked + label:hover span {
  @apply bg-violet-900;
}

.disabled input[type="radio"]:disabled {
  @apply bg-gray-500 text-gray-800;
}
</style>
